import { ICONS } from './icons';

interface IDropdownItem {
	readonly icon?: keyof typeof ICONS | null;
	readonly text?: null | string;
	readonly value: unknown;
}

/**
 * The DropdownItem is the "item" we use as the data provider in the Dropdown component.
 *
 * Both the icon and the text are optional, so an Item can show an icon and a text, only a text, or only an icon.
 *
 * Example:
 *
 * ```typescript
 * dropdown.data = [new DropdownItem({ text: 'The text we want to show', value: 'some value' }), new DropdownItem({ text: 'Another text we want to show', value: false }), new DropdownItem({ icon: 'alert-circle', text: 'With an icon', value: 1 }), new DropdownItem({ icon: 'alert-circle', value: 2 })];
 * ```
 */
export default class DropdownItem implements IDropdownItem {
	/**
	 * The optional icon is shown to the left of the text in the DropdownItemRenderer.
	 *
	 * Is null when no icon name was passed to the constructor.
	 */
	public get icon(): keyof typeof ICONS | null {
		return this.#icon;
	}

	/**
	 * The optional text is shown to the right of the icon in the DropdownItemRenderer.
	 *
	 * Is null when no text was passed to the constructor.
	 */
	public get text(): null | string {
		return this.#text;
	}

	public get value(): unknown {
		return this.#value;
	}

	#icon: keyof typeof ICONS | null;
	#text: null | string;
	#value: unknown;
	public constructor({ icon = null, text = null, value }: IDropdownItem) {
		assertIsValidIconName(icon);
		assertIsStringOrNull(text);
		this.#icon = icon;
		this.#text = text;
		this.#value = value;
	}
}

function assertIsStringOrNull(value: unknown): asserts value is null | string {
	if (typeof value === 'string' || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string or null`);
}

function assertIsValidIconName(value: unknown): asserts value is keyof typeof ICONS | null {
	if ((typeof value === 'string' && value in ICONS) || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string and in ICONS object or null`);
}
